import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import DatePicker from 'shared_items/DatePicker';
import CalendarEventItem from './CalendarEventItem';
import { getOrderedCellEvents } from '../viewServices/calendar_day_cell_order';
import { getMonthCellMultiDaySegment } from '../viewServices/calendar_grid_utils';
import { PLANNER_TIME_FORMAT_24 } from '../../moduleServices/planner_time_format';
import { formatDateLocal } from 'shared_items/formatConventions';

const SHEET_TRANSITION_MS = 300;

/** Min height of the scrollable list region — same for 0 vs 1+ events so the shell does not shrink. */
const DAY_SHEET_BODY_MIN_CLASS = 'min-h-[11rem]';

/** Fixed width footer actions (aligned with ConfirmModal-style modules). */
const FOOTER_BTN_CLASS =
  'w-28 shrink-0 rounded-xl px-4 py-2 text-sm font-medium shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2';

/** @param {string} dateKey */
function parseDateKeyToLocalDate(dateKey) {
  if (!dateKey || !/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) return null;
  const [y, m, d] = dateKey.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** @param {string} dateKey @param {number} deltaDays */
function addDaysToDateKey(dateKey, deltaDays) {
  const dt = parseDateKeyToLocalDate(dateKey);
  if (!dt) return dateKey;
  dt.setDate(dt.getDate() + deltaDays);
  return formatDateLocal(dt);
}

/**
 * Bottom sheet: lista eventos de um dia + navegação por dia + ações no rodapé.
 * Sempre ancorado ao bottom (mobile e desktop), com slide up/down ao abrir/fechar.
 */
export default function DayEventsDialog({
  open,
  onClose,
  dateKey,
  dayEvents = [],
  /** Mudar dia visível mantendo o sheet aberto: `(nextKey, nextDate)` */
  onNavigateDay,
  onEventClick,
  onNewEvent,
  timeFormatPreference = PLANNER_TIME_FORMAT_24,
}) {
  const [mounted, setMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [snapshotDateKey, setSnapshotDateKey] = useState(null);
  const [snapshotEvents, setSnapshotEvents] = useState([]);

  useEffect(() => {
    if (open && dateKey) {
      setSnapshotDateKey(dateKey);
      setSnapshotEvents(dayEvents);
    }
  }, [open, dateKey, dayEvents]);

  const activeDateKey = open ? dateKey : snapshotDateKey;
  const activeEvents = open ? dayEvents : snapshotEvents;

  const ordered = useMemo(() => getOrderedCellEvents(activeEvents, false), [activeEvents]);

  const headingId = 'planner-day-events-dialog-heading';

  const requestClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (open && dateKey) {
      setMounted(true);
      setIsVisible(false);
    }
  }, [open, dateKey]);

  // Double rAF: garante um frame pintado em translate-y-full antes de animar a abertura.
  useEffect(() => {
    if (!mounted || !open || !dateKey) return undefined;

    let raf2 = 0;
    const raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => setIsVisible(true));
    });

    return () => {
      window.cancelAnimationFrame(raf1);
      if (raf2) window.cancelAnimationFrame(raf2);
    };
  }, [mounted, open, dateKey]);

  useEffect(() => {
    if (!open && mounted) {
      setIsVisible(false);
      const t = window.setTimeout(() => {
        setMounted(false);
        setSnapshotDateKey(null);
        setSnapshotEvents([]);
      }, SHEET_TRANSITION_MS);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [open, mounted]);

  const navigateBy = useCallback(
    (deltaDays) => {
      if (!activeDateKey || typeof onNavigateDay !== 'function') return;
      const nk = addDaysToDateKey(activeDateKey, deltaDays);
      const nd = parseDateKeyToLocalDate(nk);
      if (nd) onNavigateDay(nk, nd);
    },
    [activeDateKey, onNavigateDay],
  );

  const handlePickerChange = useCallback(
    (e) => {
      const nextKey = e?.target?.value;
      if (!nextKey || !/^\d{4}-\d{2}-\d{2}$/.test(String(nextKey))) return;
      const nd = parseDateKeyToLocalDate(nextKey);
      if (nd) onNavigateDay?.(nextKey, nd);
    },
    [onNavigateDay],
  );

  useEffect(() => {
    if (!mounted) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mounted, requestClose]);

  if (!mounted || !activeDateKey) return null;

  const handleNewEvent = () => {
    onNewEvent?.();
  };

  const handleRowClick = (ev) => {
    onEventClick?.(ev);
  };

  return (
    <div
      className={`fixed inset-0 z-[45] flex items-end justify-center pointer-events-none transition-opacity duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/40 pointer-events-auto"
        aria-label="Close"
        onClick={requestClose}
      />
      <div
        className={`relative z-10 flex max-h-[min(85vh,600px)] w-full max-w-md flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl pointer-events-auto transform transition-transform duration-300 ease-out ${
          isVisible ? 'translate-y-0' : 'translate-y-full'
        }`}
        onClick={(e) => e.stopPropagation()}
        role="document"
      >
        <div className="relative shrink-0 border-b border-gray-200 px-2 py-2">
          <div className="flex items-stretch gap-0.5 sm:gap-1">
            <button
              type="button"
              onClick={() => navigateBy(-1)}
              aria-label="Previous day"
              className="flex shrink-0 items-center justify-center rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
            >
              <ChevronLeftIcon className="h-5 w-5" aria-hidden />
            </button>

            <div
              id={headingId}
              className="relative flex min-w-0 flex-1 flex-col items-center justify-center px-1"
            >
              <DatePicker
                name="day_events_sheet_day"
                value={activeDateKey}
                onChange={handlePickerChange}
                mode="single"
                colorScheme="blue"
                placeholder="Choose day"
                className="mx-auto flex w-full max-w-[min(100%,14rem)] min-w-0 justify-center [&_button]:w-full [&_button]:justify-center [&_button]:truncate"
                dropdownZIndex={60}
              />
            </div>

            <button
              type="button"
              onClick={() => navigateBy(1)}
              aria-label="Next day"
              className="flex shrink-0 items-center justify-center rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
            >
              <ChevronRightIcon className="h-5 w-5" aria-hidden />
            </button>
          </div>
        </div>

        <div
          className={`min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-2 ${DAY_SHEET_BODY_MIN_CLASS}`}
        >
          {ordered.length === 0 ? (
            <p className="px-1 py-6 text-center text-sm text-gray-500">No events this day.</p>
          ) : (
            <ul className="flex flex-col gap-1.5 pb-1">
              {ordered.map((event) => (
                <li key={event.id} className="min-w-0">
                  <CalendarEventItem
                    event={event}
                    monthSegment={getMonthCellMultiDaySegment(event, activeDateKey)}
                    onClick={handleRowClick}
                    timeFormatPreference={timeFormatPreference}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-gray-200 bg-gray-50/90 px-3 py-3">
          <button
            type="button"
            onClick={requestClose}
            className={`${FOOTER_BTN_CLASS} border border-gray-300 text-gray-700 hover:bg-gray-100`}
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleNewEvent}
            className={`${FOOTER_BTN_CLASS} bg-blue-600 font-semibold text-white hover:bg-blue-700`}
          >
            New event
          </button>
        </div>
      </div>
    </div>
  );
}
